import { useState, useEffect } from 'react';
import store from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { WatchlistApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { formatDateMonthDayYear, formatCurrency } from '@apply/ui';
import { Column, Summary, TotalItem, Grouping } from 'devextreme-react/data-grid';
import { watchlistActions } from '@apply/adminStore';
import { BfiDataGrid } from '@apply/ui';
import { WatchlistActionsButton } from '../common/components/WatchlistActionsButton';
import { Tooltip } from 'devextreme-react/tooltip';
import { WatchlistPopupDialog } from '../common/components/WatchlistPopupDialog';
import { WatchlistPopupType } from '../common/WatchlistConstants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { addressTooltipText } from '../../common/SharedFunctions';

const WatchlistSearchTable = ({ watchlistId, useWatchlistDate, summaryTypeId, watchlistLoanStatusId, gridStore }) => {
    const searchState = useSelector((state) => state.searchState);
    if (!watchlistId || !summaryTypeId) {
        return null;
    }

    const watchlistApi = new WatchlistApi.WatchlistApi(applicationInsights);
    const { dispatch } = store;

    const [criteria, set_criteria] = useState({
        summaryTypeId: null,
        watchlistId: null,
        statusId: null,
        searchText: null,
    });
    const [popupState, set_popupState] = useState({
        visible: false,
    });
    const [dataStore, set_dataStore] = useState(null);

    const dataFunction = async (sc) => {
        if (!criteria || !criteria.summaryTypeId || isNaN(criteria.watchlistId) || isNaN(criteria.statusId)) {
            return null;
        }

        sc.useWatchlistDate = useWatchlistDate;
        sc.watchlistId = criteria.watchlistId;
        sc.summaryTypeId = criteria.summaryTypeId;
        sc.statusId = criteria.statusId;
        return await watchlistApi.searchWatchlist(sc);
    };

    const styles = {
        red: { color: 'red' },
    };

    const criteriaChanged = (summaryTypeId, wlId, sId, searchText) => {
        const newCriteria = {
            summaryTypeId,
            watchlistId: wlId,
            statusId: sId,
            searchText,
        };

        const oldCriteria = criteria;
        if (oldCriteria) {
            const oldJson = JSON.stringify(oldCriteria);
            const newJson = JSON.stringify(newCriteria);
            if (oldJson === newJson) {
                return;
            }
        }

        set_criteria(newCriteria);
    };

    useEffect(() => {
        if (!criteria || !gridStore) {
            return;
        }

        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.watchlists, false);
            set_dataStore(gridStore.store);
        }
    }, [criteria, gridStore]);

    useEffect(() => {
        if (!summaryTypeId || !watchlistId || !watchlistLoanStatusId) {
            return undefined;
        }
        criteriaChanged(summaryTypeId, watchlistId, watchlistLoanStatusId, searchState?.searchText?.watchlists);
    }, [summaryTypeId, watchlistId, watchlistLoanStatusId, searchState]);

    const formatDate = (dt) => {
        if (!dt) {
            return null;
        }
        return formatDateMonthDayYear(dt);
    };

    const renderPercent = (val, precision) => {
        if (!val) {
            return null;
        }
        return val.toFixed(precision) + '%';
    };

    const onPercentRender = (e, precision = 2) => {
        if (isNaN(!e.value)) {
            return null;
        }
        return renderPercent(e.value, precision);
    };

    const onCurrencyRender = (e) => {
        if (isNaN(!e.value)) {
            return null;
        }
        return formatCurrency(e.value, 0);
    };

    const onCategoryGroupRender = (e) => {
        if (!e.data.key) {
            return null;
        }
        return <div>{e.data.key}</div>;
    };

    const showDetail = (watchlistId, loanId, propertyId) => {
        dispatch(
            watchlistActions.setWatchlistLoanDetailModal({
                watchlistLoanDetailModal: { isOpen: true, watchlistId, loanId, propertyId },
            })
        );
    };

    const onRowDblClick = (e) => {
        showDetail(e.data.watchlistId, e.data.loanId, e.data.propertyId);
    };

    const onActionRender = (e) => {
        return (
            <WatchlistActionsButton
                data={e.data}
                summaryTypeId={summaryTypeId}
            />
        );
    };

    const onToPastRender = (e) => {
        if (!e.value) {
            return null;
        }
        return <span style={e.value < 0 ? styles.red : null}>{e.value.toFixed(2) + '%'}</span>;
    };

    const onMonthsRemainingRender = (e) => {
        if (!e.value) {
            return null;
        }
        return <span style={e.value < 0 ? styles.red : null}>{e.value}</span>;
    };

    const onGradeRender = (e) => {
        if (!e.value) {
            return null;
        }
        const id = 'grade_' + e.data.id;
        return (
            <>
                <div
                    id={id}
                    style={{ width: '100%' }}
                    onClick={() => {
                        set_popupState({
                            visible: true,
                            title: 'Loan Grade History',
                            type: WatchlistPopupType.GradeHistory,
                            loanId: e.data.loanId,
                            entityId: e.data.entityId,
                        });
                    }}
                >
                    <span style={{ textDecoration: 'underline' }}>{e.value}</span>
                </div>
                <Tooltip
                    target={'#' + id}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    Click to view Grade history
                </Tooltip>
            </>
        );
    };

    const onMaturityDateRender = (e) => {
        if (!e.value) {
            return null;
        }
        const id = 'md_' + e.data.id;
        return (
            <>
                <div
                    id={id}
                    style={{ width: '100%' }}
                    onClick={() => {
                        set_popupState({
                            visible: true,
                            title: 'Maturity History',
                            type: WatchlistPopupType.MaturityHistory,
                            loanId: e.data.loanId,
                            entityId: e.data.entityId,
                        });
                    }}
                >
                    <span style={{ textDecoration: 'underline' }}>{moment(e.value).format('M/DD/YYYY')}</span>
                </div>
                <Tooltip
                    target={'#' + id}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    Click to view Maturity history
                </Tooltip>
            </>
        );
    };

    const onIssuesRender = (e) => {
        const id = 'iss_' + e.data.id;
        return (
            <>
                <div
                    id={id}
                    style={{ width: '100%' }}
                    onClick={() => {
                        set_popupState({
                            visible: true,
                            title: 'Issues',
                            type: WatchlistPopupType.Issues,
                            loanId: e.data.loanId,
                            propertyId: e.data.propertyId,
                            entityId: e.data.entityId,
                        });
                    }}
                >
                    <span style={{ textDecoration: 'underline' }}>{e.value}</span>
                </div>
                <Tooltip
                    target={'#' + id}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    Click to view Issue Tables
                </Tooltip>
            </>
        );
    };

    const onEntityRender = (e) => {
        const id = 'ent_' + e.data.id;
        return (
            <>
                <div
                    id={id}
                    style={{ width: '100%' }}
                    onClick={() => {
                        set_popupState({
                            visible: true,
                            title: 'Watchlist Entity',
                            type: WatchlistPopupType.EntityLoans,
                            loanId: e.data.loanId,
                            entityId: e.data.entityId,
                        });
                    }}
                >
                    <span style={{ textDecoration: 'underline' }}>{e.value}</span>
                </div>
                <Tooltip
                    target={'#' + id}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    Click to view all Loans in this watchlist for the Entity
                </Tooltip>
            </>
        );
    };

    const onBFINumberRender = (e) => {
        const targetId = `ac_${e.data.id}`;
        const text = addressTooltipText(e.data.propertyAddress);
        return (
            <>
                <div id={targetId}>{e.value}</div>
                {e.data.propertyId && (
                    <Tooltip
                        target={'#' + targetId}
                        showEvent='mouseenter'
                        hideEvent='mouseleave'
                        hideOnOutsideClick={false}
                    >
                        {text}
                    </Tooltip>
                )}
            </>
        );
    };

    const onFundedRender = (e) => {
        if (!e.value) {
            return 'Not Funded';
        }

        const id = 'fnd_' + e.data.id;
        return (
            <>
                <div
                    id={id}
                    style={{ width: '100%' }}
                    onClick={() => {
                        set_popupState({
                            visible: true,
                            title: 'Draw History',
                            type: WatchlistPopupType.FundingHistory,
                            loanId: e.data.loanId,
                            entityId: e.data.entityId,
                        });
                    }}
                >
                    <span style={{ textDecoration: 'underline' }}>
                        {formatCurrency(e.value, 0)} [
                        {e.data.commitment > 0 ? (e.value / e.data.commitment).toFixed(2) + '%' : '0%'}]
                    </span>
                </div>
                <Tooltip
                    target={'#' + id}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    Click to view Draw History
                </Tooltip>
            </>
        );
    };

    const onLiquidityRender = (e) => {
        if (!e.value) {
            return null;
        }

        const id = 'liq_' + e.data.id;
        return (
            <>
                <div
                    id={id}
                    style={{ width: '100%' }}
                    onClick={() => {
                        set_popupState({
                            visible: true,
                            title: 'Liquidity History',
                            type: WatchlistPopupType.LiquidityHistory,
                            loanId: e.data.loanId,
                            entityId: e.data.entityId,
                        });
                    }}
                >
                    <span style={{ textDecoration: 'underline' }}>{formatCurrency(e.value, 0)}</span>
                </div>
                <Tooltip
                    target={'#' + id}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    Click to view Loan Liquidity History
                </Tooltip>
            </>
        );
    };

    const onLtvRender = (e) => {
        if (!e.value) {
            return null;
        }

        const id = 'ltv_' + e.data.id;
        const title = e.data.propertyId ? 'Appraisals History - Property' : 'Appraisals History - Loan';
        return (
            <>
                <div
                    id={id}
                    style={{ width: '100%' }}
                    onClick={() => {
                        set_popupState({
                            visible: true,
                            title: title,
                            type: WatchlistPopupType.AppraisalsHistory,
                            loanId: e.data.loanId,
                            propertyId: e.data.propertyId,
                            entityId: e.data.entityId,
                        });
                    }}
                >
                    <span style={{ textDecoration: 'underline' }}>{onPercentRender(e, 1)}</span>
                </div>
                <Tooltip
                    target={'#' + id}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    Click to view {e.data.propertyId ? 'Property' : 'Loan'} Appraisal History
                </Tooltip>
            </>
        );
    };

    const onPopupClose = () => {
        set_popupState({ visible: false });
    };

    const onRowPrepared = (e) => {
        if (e.rowType !== 'data') {
            return;
        }
        if (e.data.groupColor) {
            e.rowElement.style.backgroundColor = e.data.groupColor;
        }
    };

    const onPrimaryLoan = (e) => {
        if (e.data.groupId && e.data.groupSortOrder === 1) {
            return (
                <FontAwesomeIcon
                    style={{ color: 'darkblue' }}
                    icon={faCircle}
                    title='Primary loan in Group'
                    size='2xs'
                />
            );
        }
        return null;
    };

    if (!dataStore) {
        return;
    }

    return (
        <>
            <BfiDataGrid
                keyExpr='id'
                dataSource={dataStore}
                applicationInsights={applicationInsights}
                columnMinWidth={20}
                columnResizingMode={'nextColumn'}
                remoteOperations={true}
                rowAlternationEnabled={false}
                showBorders={true}
                showColumnHeaders={true}
                showRowLines={true}
                noDataText='No loans of this status for the selected watchlist'
                onRowDblClick={onRowDblClick}
                defaultPageSize={0}
                pagerVisible={false}
                width='100%'
                height={450}
                onRowPrepared={onRowPrepared}
                selectionMode='none'
            >
                <Grouping autoExpandAll={true} />
                <Column
                    dataField='key'
                    caption='CategoryGroup'
                    groupIndex={0}
                    groupCellRender={onCategoryGroupRender}
                    autoExpandGroup={true}
                />
                <Column
                    width={20}
                    alignment={'middle'}
                    cellRender={onPrimaryLoan}
                />
                <Column
                    dataField='entityName'
                    caption='Borrower'
                    cellRender={onEntityRender}
                />
                <Column
                    dataField='bfiNumber'
                    caption='BFI #'
                    dataType='string'
                    width={90}
                    cellRender={onBFINumberRender}
                />
                <Column
                    dataField='noteDate'
                    caption='Note Date'
                    dataType='date'
                    width={80}
                />
                <Column
                    dataField='totalTermInMonths'
                    caption='Total Term'
                    width={80}
                    alignment='left'
                />
                <Column
                    dataField='toPast'
                    caption='To/Past'
                    width={70}
                    alignment='left'
                    cellRender={onToPastRender}
                />
                <Column
                    dataField='maturityDate'
                    caption='Mat. Date'
                    width={80}
                    cellRender={onMaturityDateRender}
                />
                <Column
                    dataField='monthsRemaining'
                    caption='Tm Lft'
                    width={60}
                    alignment='left'
                    cellRender={onMonthsRemainingRender}
                />
                <Column
                    dataField='ltv'
                    caption='LTV'
                    width={80}
                    cellRender={onLtvRender}
                    alignment='left'
                />
                <Column
                    dataField='commitment'
                    caption='Committed'
                    width={90}
                    alignment='left'
                    cellRender={onCurrencyRender}
                />
                <Column
                    dataField='funded'
                    caption='Funded'
                    width={120}
                    alignment='left'
                    cellRender={onFundedRender}
                />
                <Column
                    dataField='issue'
                    caption='Issue(s)'
                    alignment='left'
                    allowSorting={false}
                    cellRender={onIssuesRender}
                />
                <Column
                    dataField='cureCount'
                    caption='Cured'
                    alignment='left'
                    allowSorting={false}
                    width={60}
                />
                <Column
                    dataField='other'
                    caption='Other'
                    alignment='left'
                    width={60}
                />
                <Column
                    dataField='loanGrade'
                    caption='Grade'
                    width={90}
                    alignment='left'
                    cellRender={onGradeRender}
                />
                <Column
                    dataField='issueDate'
                    caption='Issue Date'
                    dataType='date'
                    width={90}
                />
                <Column
                    dataField='poc'
                    caption='POC'
                    alignment='left'
                    width={50}
                />
                <Column
                    dataField='liquidity'
                    caption='Liquidity'
                    width={80}
                    alignment='left'
                    cellRender={onLiquidityRender}
                />
                <Column
                    dataField='notes'
                    caption='Notes'
                    width={100}
                    allowSorting={false}
                />
                <Column
                    cellRender={onActionRender}
                    width={50}
                    alignment='center'
                    allowSorting={false}
                />
                <Summary
                    calculateCustomSummary={(e) => {
                        return null;
                    }}
                >
                    <TotalItem
                        column='commitment'
                        summaryType='sum'
                        customizeText={onCurrencyRender}
                    />
                    <TotalItem
                        column='funded'
                        summaryType='sum'
                        customizeText={onCurrencyRender}
                    />
                </Summary>
            </BfiDataGrid>
            <WatchlistPopupDialog
                isOpen={popupState.visible}
                title={popupState.title}
                type={popupState.type}
                loanId={popupState.loanId}
                propertyId={popupState.propertyId}
                entityId={popupState.entityId}
                watchlistId={watchlistId}
                onClose={onPopupClose}
            />
        </>
    );
};

export default WatchlistSearchTable;
